/**
 * Validación en tiempo real del wizard (gates + feedback). Subconjunto del
 * motor server-side — el resultado oficial lo calcula wio.suitability.api;
 * aquí solo bloqueamos avance y pintamos avisos. Portado de wio.funds.
 */
import type {
  BehaviorCode,
  ObjectiveCode,
  SuitabilityAnswers,
} from './answers';

export interface CoherenceError {
  id: string;
  /**
   * Clave i18n del texto, no el texto: la regla se decide aquí y la redacción en el
   * catálogo, igual que hace el motor de Lift con sus códigos de aviso.
   */
  messageKey: string;
  affectedFields: string[];
}

/** Misma matriz que el server (P7 × P10), en clave ok/blocked. */
const P7_P10_MATRIX: Record<
  ObjectiveCode,
  Record<BehaviorCode, 'ok' | 'blocked'>
> = {
  Preserve: {
    SellAll: 'ok',
    SellPart: 'ok',
    Hold: 'blocked',
    BuyMore: 'blocked',
  },
  SlightGrowth: {
    SellAll: 'ok',
    SellPart: 'ok',
    Hold: 'ok',
    BuyMore: 'blocked',
  },
  Growth: {
    SellAll: 'blocked',
    SellPart: 'ok',
    Hold: 'ok',
    BuyMore: 'ok',
  },
  Maximize: {
    SellAll: 'blocked',
    SellPart: 'blocked',
    Hold: 'ok',
    BuyMore: 'ok',
  },
};

/** Errores de coherencia para mostrar mientras el usuario rellena. */
export const getCoherenceErrors = (
  answers: SuitabilityAnswers,
): CoherenceError[] => {
  const errors: CoherenceError[] = [];

  if (answers.p11 === 'Over75' && answers.p6 === 'Long') {
    errors.push({
      id: 'C1',
      messageKey: 'wioCapital.coherence.C1',
      affectedFields: ['p6', 'p11'],
    });
  }
  if (
    answers.p7 === 'Preserve' &&
    answers.p9 &&
    answers.p9 !== 'Minimal'
  ) {
    errors.push({
      id: 'C2',
      messageKey: 'wioCapital.coherence.C2',
      affectedFields: ['p7', 'p9'],
    });
  }
  if (
    answers.p7 === 'SlightGrowth' &&
    (answers.p9 === 'Medium' || answers.p9 === 'High')
  ) {
    errors.push({
      id: 'C3',
      messageKey: 'wioCapital.coherence.C3',
      affectedFields: ['p7', 'p9'],
    });
  }
  if (
    answers.p7 &&
    answers.p10 &&
    P7_P10_MATRIX[answers.p7][answers.p10] === 'blocked'
  ) {
    errors.push({
      id: 'P7P10',
      messageKey: 'wioCapital.coherence.P7P10',
      affectedFields: ['p7', 'p10'],
    });
  }

  return errors;
};

export const getIncompatibleP10Options = (
  p7: SuitabilityAnswers['p7'],
): string[] => {
  if (!p7) return [];
  return Object.entries(P7_P10_MATRIX[p7])
    .filter(([, v]) => v === 'blocked')
    .map(([k]) => k);
};

export const getIncompatibleP9Options = (
  p7: SuitabilityAnswers['p7'],
): string[] => {
  if (!p7) return [];
  if (p7 === 'Preserve')
    return ['Low', 'Medium', 'High'];
  if (p7 === 'SlightGrowth') return ['Medium', 'High'];
  return [];
};

// ─── Completitud por bloque ──────────────────────────────────────────

export const isBlock1Complete = (answers: SuitabilityAnswers): boolean => {
  if (!answers.p1) return false;
  if (answers.p1 === 'Direct') return true; // bypass profesional
  if (!answers.p2) return false;
  if (!answers.p3_1 || !answers.p3_2 || !answers.p3_3) return false;
  const p4Keys: (keyof SuitabilityAnswers)[] = [
    'p4_1_1',
    'p4_1_2',
    'p4_2_1',
    'p4_2_2',
    'p4_3_1',
    'p4_3_2',
    'p4_4_1',
    'p4_4_2',
    'p4_5_1',
    'p4_5_2',
  ];
  if (p4Keys.some((key) => !answers[key])) return false;
  return true;
};

export const isBlock2Complete = (answers: SuitabilityAnswers): boolean =>
  !!(
    answers.p6 &&
    answers.p7 &&
    answers.p8 &&
    answers.p9 &&
    answers.p10 &&
    answers.p11
  );

export const isBlock3Complete = (answers: SuitabilityAnswers): boolean =>
  !!(answers.p12 && answers.p13 && answers.p14 && answers.p15);
